import React, { forwardRef, useId } from 'react';
import { AlertCircle, CheckCircle, Info } from 'lucide-react';
import { cn } from './tokens';

// =============================================================================
// SHARED STYLES
// =============================================================================

const baseInputStyles = `
  w-full
  bg-slate-800 text-slate-50
  border border-slate-700
  rounded-lg
  placeholder:text-slate-400
  transition-all duration-200
  focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20
  disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-800/50
`;

const sizes = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-4 text-base',
} as const;

const textareaSizes = {
  sm: 'px-3 py-2 text-sm min-h-[80px]',
  md: 'px-4 py-3 text-sm min-h-[100px]',
  lg: 'px-4 py-3 text-base min-h-[120px]',
} as const;

// =============================================================================
// INPUT COMPONENT
// =============================================================================

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Size variant */
  size?: keyof typeof sizes;
  /** Error state */
  isInvalid?: boolean;
  /** Left addon/icon */
  leftElement?: React.ReactNode;
  /** Right addon/icon */
  rightElement?: React.ReactNode;
}

/**
 * Text input component with consistent styling
 *
 * @example
 * <Input placeholder="Enter your name" />
 *
 * @example
 * // With icon
 * <Input
 *   placeholder="Search..."
 *   leftElement={<Search size={16} className="text-slate-400" />}
 * />
 *
 * @example
 * // Invalid state
 * <Input placeholder="Email" isInvalid />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = 'md',
      isInvalid = false,
      leftElement,
      rightElement,
      className,
      ...props
    },
    ref
  ) => {
    if (leftElement || rightElement) {
      return (
        <div className="relative">
          {leftElement && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
              {leftElement}
            </div>
          )}
          <input
            ref={ref}
            className={cn(
              baseInputStyles,
              sizes[size],
              leftElement ? 'pl-10' : null,
              rightElement ? 'pr-10' : null,
              isInvalid ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : null,
              className
            )}
            {...props}
          />
          {rightElement && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {rightElement}
            </div>
          )}
        </div>
      );
    }

    return (
      <input
        ref={ref}
        className={cn(
          baseInputStyles,
          sizes[size],
          isInvalid ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : null,
          className
        )}
        {...props}
      />
    );
  }
);

Input.displayName = 'Input';

// =============================================================================
// TEXTAREA COMPONENT
// =============================================================================

export interface TextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  /** Size variant */
  size?: keyof typeof textareaSizes;
  /** Error state */
  isInvalid?: boolean;
  /** Auto-resize based on content */
  autoResize?: boolean;
}

/**
 * Textarea component with consistent styling
 *
 * @example
 * <TextArea placeholder="Enter description..." rows={4} />
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      size = 'md',
      isInvalid = false,
      autoResize = false,
      className,
      onChange,
      ...props
    },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (autoResize) {
        e.target.style.height = 'auto';
        e.target.style.height = `${e.target.scrollHeight}px`;
      }
      onChange?.(e);
    };

    return (
      <textarea
        ref={ref}
        onChange={handleChange}
        className={cn(
          baseInputStyles,
          textareaSizes[size],
          'resize-y',
          autoResize ? 'resize-none overflow-hidden' : null,
          isInvalid ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : null,
          className
        )}
        {...props}
      />
    );
  }
);

TextArea.displayName = 'TextArea';

// =============================================================================
// SELECT COMPONENT
// =============================================================================

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Size variant */
  size?: keyof typeof sizes;
  /** Error state */
  isInvalid?: boolean;
  /** Placeholder option */
  placeholder?: string;
  /** Options to render */
  options?: Array<{ value: string; label: string; disabled?: boolean }>;
}

/**
 * Select dropdown with consistent styling
 *
 * @example
 * <Select
 *   placeholder="Choose an option"
 *   options={[
 *     { value: 'a', label: 'Option A' },
 *     { value: 'b', label: 'Option B' },
 *   ]}
 * />
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      size = 'md',
      isInvalid = false,
      placeholder,
      options,
      children,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div className="relative">
        <select
          ref={ref}
          className={cn(
            baseInputStyles,
            sizes[size],
            'appearance-none cursor-pointer pr-10',
            isInvalid ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : null,
            className
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options
            ? options.map((opt) => (
                <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                  {opt.label}
                </option>
              ))
            : children}
        </select>
        {/* Dropdown arrow */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg
            className="w-4 h-4 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
    );
  }
);

Select.displayName = 'Select';

// =============================================================================
// FORM FIELD WRAPPER
// =============================================================================

export interface FormFieldProps {
  /** Field label */
  label?: React.ReactNode;
  /** Field description/helper text */
  description?: string;
  /** Error message */
  error?: string;
  /** Success message */
  success?: string;
  /** Mark as required */
  required?: boolean;
  /** The form control */
  children: React.ReactNode;
  /** Additional class names */
  className?: string;
}

/**
 * Form field wrapper with label, description, and error handling
 *
 * @example
 * <FormField label="Email" required error="Invalid email format">
 *   <Input type="email" isInvalid />
 * </FormField>
 */
export const FormField: React.FC<FormFieldProps> = ({
  label,
  description,
  error,
  success,
  required,
  children,
  className,
}) => {
  const id = useId();

  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-slate-300"
        >
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
      )}

      {description && !error && (
        <p className="text-xs text-slate-400 flex items-center gap-1">
          <Info size={12} />
          {description}
        </p>
      )}

      {/* Clone child to add id */}
      {React.isValidElement(children)
        ? React.cloneElement(children as React.ReactElement<{ id?: string }>, { id })
        : children}

      {error && (
        <p className="text-xs text-red-400 flex items-center gap-1">
          <AlertCircle size={12} />
          {error}
        </p>
      )}

      {success && !error && (
        <p className="text-xs text-green-400 flex items-center gap-1">
          <CheckCircle size={12} />
          {success}
        </p>
      )}
    </div>
  );
};

// =============================================================================
// CHECKBOX COMPONENT
// =============================================================================

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Checkbox label */
  label?: string;
  /** Description below label */
  description?: string;
}

/**
 * Checkbox with optional label
 *
 * @example
 * <Checkbox label="Accept terms and conditions" />
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, description, className, ...props }, ref) => {
    const id = useId();

    return (
      <div className={cn('flex items-start gap-3', className)}>
        <input
          ref={ref}
          id={id}
          type="checkbox"
          className={cn(
            'mt-0.5 h-4 w-4 rounded border-slate-600 bg-slate-800',
            'text-accent focus:ring-accent focus:ring-offset-0',
            'cursor-pointer'
          )}
          {...props}
        />
        {(label || description) && (
          <div>
            {label && (
              <label
                htmlFor={id}
                className="text-sm font-medium text-slate-300 cursor-pointer"
              >
                {label}
              </label>
            )}
            {description && (
              <p className="text-xs text-slate-400 mt-0.5">{description}</p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

// =============================================================================
// RADIO GROUP
// =============================================================================

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  /** Radio group name */
  name: string;
  /** Current value */
  value?: string;
  /** Change handler */
  onChange?: (value: string) => void;
  /** Options to render */
  options: RadioOption[];
  /** Layout direction */
  orientation?: 'horizontal' | 'vertical';
  /** Additional class names */
  className?: string;
}

/**
 * Radio button group
 *
 * @example
 * <RadioGroup
 *   name="plan"
 *   value={selectedPlan}
 *   onChange={setSelectedPlan}
 *   options={[
 *     { value: 'free', label: 'Free' },
 *     { value: 'pro', label: 'Pro', description: '$9/month' },
 *   ]}
 * />
 */
export const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  value,
  onChange,
  options,
  orientation = 'vertical',
  className,
}) => {
  return (
    <div
      className={cn(
        'flex gap-3',
        orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap',
        className
      )}
      role="radiogroup"
    >
      {options.map((option) => (
        <label
          key={option.value}
          className={cn(
            'flex items-start gap-3 cursor-pointer',
            option.disabled ? 'opacity-50 cursor-not-allowed' : null
          )}
        >
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            disabled={option.disabled}
            onChange={(e) => onChange?.(e.target.value)}
            className={cn(
              'mt-0.5 h-4 w-4 border-slate-600 bg-slate-800',
              'text-accent focus:ring-accent focus:ring-offset-0'
            )}
          />
          <div>
            <span className="text-sm font-medium text-slate-300">{option.label}</span>
            {option.description && (
              <p className="text-xs text-slate-400 mt-0.5">{option.description}</p>
            )}
          </div>
        </label>
      ))}
    </div>
  );
};

export default Input;
